import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { useDashboards } from "@/shared/domain/dashboards/hooks";
import { Goto } from "@/shared/navigation/Goto";

import { findScopeDrafts } from "../dashboardDrafts/dashboardDraft";
import {
  applyDefinitionTimeParams,
  isDashboardDefinition,
} from "../DashboardHeader/DashboardParams";

import {
  clearRememberedDashboardId,
  loadRememberedDashboardId,
  resolveInitialSelection,
} from "./dashboardSelection";

/**
 * The user's explicit in-session choice, as far as this hook needs to know
 * it: `unsaved-view` marks the CURRENT bare route as the user's own
 * destination ("New dashboard", or the post-delete fallback), so re-entering
 * it must not re-resolve; `dashboard` and `none` carry no such claim — a
 * bare route reached in those states came from outside (nav link,
 * back/forward) and re-resolves. Owned by `MetricsDashboard` as a ref so the
 * hook reads the value current at navigation time, without effect churn.
 */
export type ExplicitDashboardSelection =
  | { kind: "none" }
  | { kind: "unsaved-view" }
  | { kind: "dashboard"; dashboardId: string };

export interface UseInitialDashboardSelectionOptions {
  orgId: string | undefined;
  /** Set when the dashboard is the device-detail Metrics tab. */
  deviceId?: string;
  /** The current normalized `:dashboardId?` path segment (null = bare route). */
  activeDashboardId: string | null;
  /** False disables the heuristic entirely (e.g. non-routed mounts). */
  enabled: boolean;
  /** The user's explicit in-session selection ({@link ExplicitDashboardSelection}). */
  explicitSelectionRef: React.MutableRefObject<ExplicitDashboardSelection>;
  /** The org's metric-definition count, or undefined while loading. A
   *  dashboard result only navigates when metrics exist — otherwise the
   *  onboarding gate (priority 4) owns the screen and a URL write would be
   *  pointless. */
  definitionsCount: number | undefined;
  /** The resolution chose the priority-3 Dashboard Showcase: the owner seeds
   *  the showcase layout for the unsaved view. */
  onShowcase: () => void;
}

/**
 * Applies the landing-time dashboard selection (`resolveInitialSelection`) on
 * a bare metrics route. Armed when the pathname becomes a bare metrics route
 * without a `:dashboardId` segment — the initial mount, and later navigations
 * onto it (the sidebar Metrics link, backing out of a dashboard). The org- and
 * device-level routes share one route object (`:dashboardId?`), so re-entering
 * the bare route does NOT remount this component; arming keys off
 * `location.pathname` instead of mount.
 *
 * Two cases are exempt: a bare route the user chose as the unsaved view
 * ({@link ExplicitDashboardSelection} reads `unsaved-view` — "New dashboard"
 * and the post-delete fallback must keep their empty canvas), and
 * non-navigations (list refetches/polls, in-place re-renders — the pathname is
 * unchanged). A deep link (or back/forward onto a dashboard URL) never triggers
 * it.
 *
 * Outcomes:
 * - `dashboard` (priorities 1–2) → navigate to the scope's dashboard path
 *   with `{ replace: true }` — an automatic redirect must not trap Back on
 *   the pre-redirect bare URL (explicit picker loads push instead). The
 *   navigation is skipped if the user selected a dashboard while the list
 *   was loading, or if the org has no metric definitions.
 * - `showcase` (priority 3) → `onShowcase()` — no navigation.
 * - `unsaved-draft` → nothing to do: the unsaved view is already the target
 *   and the normal seeding path raises the draft-restore prompt.
 * - Stale stored ids are cleared (`clearRemembered`) as flagged.
 *
 * Returns `deciding: true` while the outcome is unresolved — the owner
 * renders its spinner instead of the canvas so the wrong state is never
 * painted (the showcase invariant's visual half).
 *
 * TODO(Pratik): This logic should move to the router loader
 */
export function useInitialDashboardSelection({
  orgId,
  deviceId,
  activeDashboardId,
  enabled,
  explicitSelectionRef,
  definitionsCount,
  onShowcase,
}: UseInitialDashboardSelectionOptions): { deciding: boolean } {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [phase, setPhase] = useState<"deciding" | "done">("done");

  const [handledPathname, setHandledPathname] = useState<string | null>(null);
  if (handledPathname !== location.pathname) {
    setHandledPathname(location.pathname);
    if (
      enabled &&
      activeDashboardId === null &&
      explicitSelectionRef.current.kind !== "unsaved-view"
    ) {
      setPhase("deciding");
    }
  }

  const dashboardsQuery = useDashboards();
  const listReady = dashboardsQuery.isSuccess && !dashboardsQuery.hasNextPage;
  const dashboards = listReady ? dashboardsQuery.data : undefined;
  const dashboardsErrored = dashboardsQuery.isError;

  // The latest values, readable from the apply effect without re-triggering
  // it (the effect must fire on data arrival, not on every URL change).
  const activeDashboardIdRef = useRef(activeDashboardId);
  activeDashboardIdRef.current = activeDashboardId;
  const searchRef = useRef(searchParams);
  searchRef.current = searchParams;
  const onShowcaseRef = useRef(onShowcase);
  onShowcaseRef.current = onShowcase;

  useEffect(() => {
    if (phase !== "deciding") {
      return;
    }
    if (orgId === undefined) {
      return; // The remembered id and drafts are org-keyed — wait.
    }
    // The user acted while we were deciding (picked a dashboard, followed a
    // link): their navigation wins, nothing to apply.
    if (activeDashboardIdRef.current !== null) {
      setPhase("done");
      return;
    }
    const resolution = resolveInitialSelection({
      dashboards,
      dashboardsErrored,
      rememberedDashboardId: loadRememberedDashboardId(orgId),
      scopeDrafts: findScopeDrafts({ orgId, deviceId }),
    });
    if (resolution.kind === "pending") {
      return; // Re-runs when the list settles.
    }
    if (resolution.kind === "unsaved-draft") {
      // Already on the unsaved view; the seeding path will raise the
      // draft-restore prompt.
      setPhase("done");
      return;
    }
    if (resolution.kind === "showcase") {
      if (resolution.clearRemembered) {
        clearRememberedDashboardId(orgId);
      }
      onShowcaseRef.current();
      setPhase("done");
      return;
    }
    // resolution.kind === "dashboard"
    if (resolution.clearRemembered) {
      clearRememberedDashboardId(orgId);
    }
    if (definitionsCount === undefined) {
      return; // Wait: navigating is pointless if onboarding will render.
    }
    if (definitionsCount > 0) {
      // The selected dashboard dictates the time params: overlay its
      // definition's range/period onto the carried-over query string. The
      // seeding effect hydrates them too, but only on a FIRST seed — a
      // revisit to an already-seeded dashboard would otherwise keep
      // whatever params the bare entry carried. (`kind: "dashboard"` only
      // resolves from the drained list, so the record lookup can't miss;
      // an invalid stored definition just leaves the params as-is.)
      const record = dashboards?.find(
        (d) => d.dashboard_id === resolution.dashboardId,
      );
      const search =
        record !== undefined &&
        isDashboardDefinition(record.dashboard_definition)
          ? applyDefinitionTimeParams(
              searchRef.current,
              record.dashboard_definition,
            )
          : searchRef.current;
      navigate(
        {
          pathname: Goto.createMetricsDashboardRoute({
            deviceId,
            dashboardId: resolution.dashboardId,
          }),
          search: search.toString(),
        },
        { replace: true },
      );
    }
    setPhase("done");
  }, [
    phase,
    orgId,
    deviceId,
    dashboards,
    dashboardsErrored,
    definitionsCount,
    navigate,
  ]);

  return { deciding: phase === "deciding" };
}
